<template>
  <div class="oauth-buttons" aria-label="Sign in options">
    <button
      id="google-login"
      @click="handleSocialLogin('google')"
      class="oauth-button"
      type="button"
      :disabled="userStore.loading"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Continue with Google
    </button>

    <button
      id="facebook-login"
      @click="handleSocialLogin('facebook')"
      class="oauth-button"
      type="button"
      :disabled="userStore.loading"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
      Continue with Facebook
    </button>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { landingFor, safeRedirectTarget } from '../utils/postLogin'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

async function handleSocialLogin(provider) {
  const profile = await userStore.socialLogin(provider)
  if (profile) {
    // Same continuation rules as the email flow: honor a sanitized
    // ?redirect=, thread it through consent onboarding when needed, and
    // otherwise land each persona on its home surface.
    const redirect = safeRedirectTarget(route)
    if (!profile.onboardingComplete) {
      await router.replace(redirect ? { path: '/consent', query: { next: redirect } } : '/consent')
    } else {
      await router.replace(redirect || landingFor(profile.role))
    }
  }
}
</script>

<style scoped>
.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

/* Notion-like: uniform quiet outline buttons with the provider mark. */
.oauth-button {
  align-items: center;
  background: #fff;
  border: 1px solid #d6d3d1;
  border-radius: 0.5rem;
  color: #191918;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 500;
  gap: 0.65rem;
  justify-content: center;
  min-height: 2.75rem;
  transition: background 0.15s, border-color 0.15s;
  width: 100%;
}

.oauth-button:hover:not(:disabled) {
  background: #f7f7f5;
  border-color: #c8c4c0;
}

.oauth-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

:global(.dark-theme) .oauth-button {
  background: #182334;
  border-color: #46566f;
  color: #e2e8f0;
}

:global(.dark-theme) .oauth-button:hover:not(:disabled) {
  background: #1e2c42;
}
</style>
